import { getUserId } from './utils';
import { d1Get, d1List, d1Upsert } from './d1-storage';
import type { CoupleData, TriedClip } from '@/types';

const API_BASE = '/api/storage';

// Check if we're running with a backend (Cloudflare) or locally
const hasBackend = (): boolean => {
  // In development, we may not have the API available
  // Check if we should use localStorage fallback
  return !window.location.hostname.includes('localhost') ||
         import.meta.env.PROD;
};

// LocalStorage fallback for development
const localStorageFallback = {
  async get(key: string, shared: boolean): Promise<{ value: string | null }> {
    const storageKey = shared ? key : `user:${getUserId()}:${key}`;
    const value = localStorage.getItem(storageKey);
    return { value };
  },

  async set(key: string, value: string, shared: boolean): Promise<void> {
    const storageKey = shared ? key : `user:${getUserId()}:${key}`;
    localStorage.setItem(storageKey, value);
  }
};

// Cloudflare KV-backed storage
const cloudflareStorage = {
  async get(key: string, shared: boolean): Promise<{ value: string | null }> {
    try {
      const userId = shared ? '' : getUserId();
      const params = new URLSearchParams({ shared: String(shared) });
      if (!shared) params.set('userId', userId);

      const res = await fetch(`${API_BASE}/${key}?${params}`);
      if (!res.ok) throw new Error('Storage fetch failed');
      return res.json();
    } catch (err) {
      console.warn('Cloudflare storage get failed, using localStorage:', err);
      return localStorageFallback.get(key, shared);
    }
  },

  async set(key: string, value: string, shared: boolean): Promise<void> {
    try {
      const userId = shared ? '' : getUserId();
      const res = await fetch(`${API_BASE}/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value, shared, userId }),
      });
      if (!res.ok) {
        const errorText = await res.text().catch(() => 'Unknown error');
        throw new Error(`Storage set failed: ${res.status} - ${errorText}`);
      }
    } catch (err) {
      console.error('Cloudflare storage set failed:', err);
      // Still save to localStorage as fallback, but also re-throw so callers know it failed
      localStorageFallback.set(key, value, shared);
      throw err;
    }
  }
};

// Storage interface that matches original window.storage API
export const storage = {
  async get(key: string, shared: boolean = true): Promise<{ value: string | null }> {
    if (hasBackend()) {
      return cloudflareStorage.get(key, shared);
    }
    return localStorageFallback.get(key, shared);
  },

  async set(key: string, value: string, shared: boolean = true): Promise<void> {
    if (hasBackend()) {
      return cloudflareStorage.set(key, value, shared);
    }
    return localStorageFallback.set(key, value, shared);
  }
};

// Make storage available globally for compatibility with original code
if (typeof window !== 'undefined') {
  (window as unknown as { storage: typeof storage }).storage = storage;
}

// Legacy key helpers — kept for reference but no longer used by components
export const getCoupleKey = (coupleId: string) => `vb-couple-data:${coupleId}`;
export const getReviewerCoupleBackupKey = (reviewerId: string) => `vb-reviewer-couple:${reviewerId}`;

// Get couple by sharing code (VBC-XXXXXX) - used by recommenders
export async function getCoupleByCode(vbcCode: string): Promise<CoupleData | null> {
  const code = vbcCode.toUpperCase().trim();
  const couple = await d1Get<CoupleData>('couples', code);
  if (!couple) return null;
  // Attach tried clips
  const triedClips = await d1List<TriedClip>('tried_clips', { coupleId: code });
  return { ...couple, tried: triedClips };
}

// Get couple by access code (VBA-XXXXXX) - used for login
export async function getCoupleByAccessCode(vbaCode: string): Promise<CoupleData | null> {
  const code = vbaCode.toUpperCase().trim();
  const results = await d1List<CoupleData>('couples', { accessCode: code });
  if (results.length === 0) return null;
  const couple = results[0]!;
  // Attach tried clips
  const triedClips = await d1List<TriedClip>('tried_clips', { coupleId: couple.coupleId });
  return { ...couple, tried: triedClips };
}

export async function saveCoupleByCode(data: CoupleData): Promise<void> {
  // Only upsert the couple row — tried clips are synced by saveCoupleData in App.tsx
  const { tried, ...coupleFields } = data;
  await d1Upsert('couples', coupleFields);
}

export default storage;
